/**
 * Game type definitions
 */
export type GameType = 'daily' | 'future-daily' | 'random' | 'custom'

/**
 * Determine the type of game being played
 *
 * @param day - The day offset (0 = today, positive = future days, negative = past days)
 * @param random - The random index (-1 = not random)
 * @param hasCustomSolution - Whether there's a custom solution from URL
 * @returns The game type
 */
export function getGameType(
  day: number,
  random: number,
  hasCustomSolution: boolean = false
): GameType {
  // Custom puzzle from URL
  if (hasCustomSolution) {
    return 'custom'
  }

  // Random game
  if (random > -1) {
    return 'random'
  }

  // Daily game - day 0 means today
  if (day === 0) {
    return 'daily'
  }

  // Future daily game
  return 'future-daily'
}

/**
 * Check if the current game is the current day's daily game
 * Only returns true for day === 0 and random === -1 (no random, no custom)
 *
 * @param day - The day offset from today
 * @param random - The random index (-1 if not random)
 * @param hasCustomSolution - Whether there's a custom solution from URL
 * @returns true if this is today's daily game
 */
export function isCurrentDailyGame(
  day: number,
  random: number,
  hasCustomSolution: boolean = false
): boolean {
  return getGameType(day, random, hasCustomSolution) === 'daily'
}

/**
 * Check if the game should count for championship/room rankings
 * Currently only daily games count
 *
 * @param day - The day offset from today
 * @param random - The random index (-1 if not random)
 * @param hasCustomSolution - Whether there's a custom solution from URL
 * @returns true if this game should count for rankings
 */
export function shouldCountForRankings(
  day: number,
  random: number,
  hasCustomSolution: boolean = false
): boolean {
  return isCurrentDailyGame(day, random, hasCustomSolution)
}
